package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * Compact event card for horizontal rails (Featured / "I'm going"). Everything lives on or right
 * under the image — date pill and RSVP toggle overlay the cover — so the card stays short and the
 * rail reads as a strip of posters rather than a row of full-size cards. Copy is caller-owned.
 */
@Composable
fun EventRailCard(
    title: String,
    dateTime: String,
    venue: String,
    imageContentDescription: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    coverUrl: String? = null,
    badgeLabel: String? = null,
    /** e.g. "12 going"; renders end-aligned on the meta row when present. */
    goingCountLabel: String? = null,
    rsvpGoing: Boolean = false,
    rsvpContentDescription: String? = null,
    onToggleRsvp: (() -> Unit)? = null,
) {
    AppCard(modifier = modifier, onClick = onClick) {
        Column {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(16f / 9f),
            ) {
                BusinessCover(
                    imageUrl = coverUrl,
                    contentDescription = imageContentDescription,
                    iconName = "event",
                )
                if (badgeLabel != null) {
                    StatusBadge(
                        label = badgeLabel,
                        modifier = Modifier.align(Alignment.TopStart).padding(AppTheme.spacing.sm),
                    )
                }
                if (onToggleRsvp != null) {
                    Box(
                        modifier = Modifier
                            .align(Alignment.TopEnd)
                            .padding(AppTheme.spacing.sm)
                            .size(AppTheme.sizes.iconCircle)
                            .background(AppTheme.colors.surface, AppTheme.shapes.pill)
                            .clickable(onClick = onToggleRsvp),
                        contentAlignment = Alignment.Center,
                    ) {
                        AppIcon(
                            name = "event_available",
                            size = AppTheme.sizes.iconButton,
                            filled = rsvpGoing,
                            tint = if (rsvpGoing) AppTheme.colors.primary else AppTheme.colors.onSurfaceVariant,
                            contentDescription = rsvpContentDescription,
                        )
                    }
                }
                Box(
                    modifier = Modifier
                        .align(Alignment.BottomStart)
                        .padding(AppTheme.spacing.sm)
                        .background(AppTheme.colors.surface, AppTheme.shapes.pill)
                        .padding(horizontal = AppTheme.spacing.sm, vertical = AppTheme.spacing.xxs),
                ) {
                    Text(
                        text = dateTime,
                        style = AppTheme.typography.labelSm,
                        color = AppTheme.colors.primary,
                        maxLines = 1,
                    )
                }
            }
            Column(
                modifier = Modifier.padding(AppTheme.spacing.md),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
            ) {
                Text(
                    text = title,
                    style = AppTheme.typography.titleSm,
                    color = AppTheme.colors.onSurface,
                    minLines = 2,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    AppIcon(
                        name = "location_on",
                        size = AppTheme.sizes.iconSm,
                        tint = AppTheme.colors.onSurfaceVariant,
                    )
                    Text(
                        text = venue,
                        style = AppTheme.typography.labelSm,
                        color = AppTheme.colors.onSurfaceVariant,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f),
                    )
                    if (goingCountLabel != null) {
                        Text(
                            text = goingCountLabel,
                            style = AppTheme.typography.labelSm,
                            color = AppTheme.colors.primary,
                            maxLines = 1,
                        )
                    }
                }
            }
        }
    }
}

/**
 * Compact marketplace card for horizontal rails (Saved / Featured / seller strips). Square photo,
 * price as an overlay pill, two-line title in body type — sized so ~2.3 cards show per viewport,
 * which is what makes the rail read as scrollable. Copy is caller-owned.
 */
@Composable
fun MarketItemRailCard(
    title: String,
    price: String,
    condition: String,
    imageContentDescription: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    imageUrl: String? = null,
    badgeLabel: String? = null,
    favorite: Boolean = false,
    favoriteContentDescription: String? = null,
    onToggleFavorite: (() -> Unit)? = null,
) {
    AppCard(modifier = modifier, onClick = onClick) {
        Column {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(1f),
            ) {
                BusinessCover(
                    imageUrl = imageUrl,
                    contentDescription = imageContentDescription,
                    iconName = "shopping_bag",
                )
                if (badgeLabel != null) {
                    StatusBadge(
                        label = badgeLabel,
                        modifier = Modifier.align(Alignment.TopStart).padding(AppTheme.spacing.sm),
                    )
                }
                if (onToggleFavorite != null) {
                    Box(
                        modifier = Modifier
                            .align(Alignment.TopEnd)
                            .padding(AppTheme.spacing.sm)
                            .size(AppTheme.sizes.iconCircle)
                            .background(AppTheme.colors.surface, AppTheme.shapes.pill)
                            .clickable(onClick = onToggleFavorite),
                        contentAlignment = Alignment.Center,
                    ) {
                        AppIcon(
                            name = if (favorite) "favorite" else "favorite_border",
                            size = AppTheme.sizes.iconButton,
                            filled = favorite,
                            tint = if (favorite) AppTheme.colors.primary else AppTheme.colors.onSurfaceVariant,
                            contentDescription = favoriteContentDescription,
                        )
                    }
                }
                Box(
                    modifier = Modifier
                        .align(Alignment.BottomStart)
                        .padding(AppTheme.spacing.sm)
                        .background(AppTheme.colors.surface, AppTheme.shapes.pill)
                        .padding(horizontal = AppTheme.spacing.sm, vertical = AppTheme.spacing.xxs),
                ) {
                    Text(
                        text = price,
                        style = AppTheme.typography.labelSm,
                        color = AppTheme.colors.onSurface,
                        maxLines = 1,
                    )
                }
            }
            Column(
                modifier = Modifier.padding(AppTheme.spacing.md),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs),
            ) {
                Text(
                    text = title,
                    style = AppTheme.typography.titleSm,
                    color = AppTheme.colors.onSurface,
                    minLines = 2,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = condition,
                    style = AppTheme.typography.labelSm,
                    color = AppTheme.colors.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

@Preview
@Composable
private fun EventRailCardPreview() {
    AppTheme {
        EventRailCard(
            title = "Tirgan Festival — Iranian Arts & Culture",
            dateTime = "JUL 23 • 12:00 PM",
            venue = "Harbourfront Centre, Toronto",
            imageContentDescription = "Event cover",
            onClick = {},
            badgeLabel = "Featured",
            goingCountLabel = "12 going",
            rsvpGoing = true,
            onToggleRsvp = {},
        )
    }
}

@Preview
@Composable
private fun MarketItemRailCardPreview() {
    AppTheme {
        MarketItemRailCard(
            title = "Persian tea glasses set",
            price = "$15.00",
            condition = "Like new",
            imageContentDescription = "Marketplace item",
            onClick = {},
            favorite = true,
            onToggleFavorite = {},
        )
    }
}
